import { useRef, useEffect, useState, useCallback } from 'react'
import { useContent } from '../../contexts/ContentContext'
import { resolveImageOverlay } from '../../utils/sectionTone'

const DISPLAY_DURATION = 7000 // ms per quote
const LINE_STAGGER_MS = 220 // delay between each line animating in
const FADE_OUT_MS = 600 // fade-out before switching

/**
 * QuoteSection — featured rotating quotes.
 *
 * Content quoteSection:
 * {
 *   id?,
 *   quotes: { lines: string[], author?: string }[],
 *   // solid navy (default) OR full-bleed image variant:
 *   surface?: 'image' | 'navy',
 *   backgroundUrl?,           // enables full-bleed image
 *   overlayOpacity?: number,  // 0–1 navy/black darken (reliable rgba)
 *   overlayColor?: 'navy' | 'black',
 *   overlay?: string,         // optional Tailwind black/white class
 * }
 */
export default function QuoteSection() {
  const { quoteSection } = useContent()
  const quotes = quoteSection?.quotes
  const sectionRef = useRef(null)
  const timerRef = useRef(null)

  const [sectionVisible, setSectionVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [animKey, setAnimKey] = useState(0)
  const [fadingOut, setFadingOut] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setSectionVisible(true)
      },
      { threshold: 0.2 }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  const advance = useCallback(() => {
    if (!quotes?.length) return
    setFadingOut(true)
    setTimeout(() => {
      setActiveIndex((i) => (i + 1) % quotes.length)
      setAnimKey((k) => k + 1)
      setFadingOut(false)
    }, FADE_OUT_MS)
  }, [quotes?.length])

  useEffect(() => {
    if (!sectionVisible || !quotes?.length) return undefined
    timerRef.current = setInterval(advance, DISPLAY_DURATION)
    return () => clearInterval(timerRef.current)
  }, [sectionVisible, advance, quotes?.length])

  if (!quotes?.length) return null

  const current = quotes[activeIndex]
  const hasImage = Boolean(quoteSection.backgroundUrl)
  const imageOverlay = hasImage
    ? resolveImageOverlay(
        {
          overlayOpacity: quoteSection.overlayOpacity ?? 0.55,
          overlayColor: quoteSection.overlayColor || 'navy',
          overlay: quoteSection.overlay,
        },
        'onDark'
      )
    : null

  const sectionId = quoteSection.id || 'quote'

  return (
    <section
      data-section="QuoteSection"
      data-variant={hasImage ? 'image' : 'navy'}
      ref={sectionRef}
      id={sectionId}
      className={`relative min-h-screen flex items-center py-24 md:py-32 snap-section overflow-hidden ${
        hasImage ? '' : 'bg-color-primary'
      }`}
      data-testid="quote-section"
    >
      {hasImage ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${quoteSection.backgroundUrl})` }}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 ${imageOverlay.className || ''}`}
            style={imageOverlay.style}
            aria-hidden="true"
            data-testid="quote-section-overlay"
          />
        </>
      ) : null}

      <div className="relative z-10 w-full px-4 sm:px-6 md:px-0">
        <div className="max-w-4xl mx-auto text-center">
          <div
            className={`mb-8 md:mb-12 text-primary-on-dark transition-opacity duration-700 effect-drop-display ${
              sectionVisible && !fadingOut ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <svg className="w-8 h-8 md:w-16 md:h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
          </div>

          <blockquote
            key={animKey}
            className={`typography-quote-featured-on-dark italic effect-drop-display transition-opacity duration-[600ms] ${
              fadingOut ? 'opacity-0' : ''
            }`}
          >
            {current.lines.map((line, i) => (
              <span
                key={i}
                className="block"
                style={{
                  animation:
                    sectionVisible && !fadingOut ? `quoteLine 0.7s ease-out both` : 'none',
                  animationDelay: `${i * LINE_STAGGER_MS}ms`,
                }}
              >
                {line}
              </span>
            ))}
          </blockquote>

          <div
            key={`author-${animKey}`}
            className={`flex items-center justify-center gap-4 mt-12 md:mt-16 transition-opacity duration-700 ${
              fadingOut ? 'opacity-0' : ''
            }`}
            style={{
              animation:
                sectionVisible && !fadingOut ? `quoteLine 0.7s ease-out both` : 'none',
              animationDelay: `${current.lines.length * LINE_STAGGER_MS + 100}ms`,
            }}
          >
            <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-[var(--color-divider-on-dark)]" />
            <span className="typography-label-on-dark whitespace-nowrap">{current.author}</span>
            <div className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-[var(--color-divider-on-dark)]" />
          </div>

          {quotes.length > 1 ? (
            <div className="flex justify-center gap-2 mt-10">
              {quotes.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    clearInterval(timerRef.current)
                    setFadingOut(true)
                    setTimeout(() => {
                      setActiveIndex(i)
                      setAnimKey((k) => k + 1)
                      setFadingOut(false)
                      timerRef.current = setInterval(advance, DISPLAY_DURATION)
                    }, FADE_OUT_MS)
                  }}
                  aria-label={`Quote ${i + 1}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? 'bg-primary-on-dark scale-125'
                      : 'bg-divider-on-dark hover:bg-secondary-on-dark'
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
